import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { E入力分類, Eフェーズ種別, E集計転記科目 } from '@/openapi/enum';
import {
  CデータPlbscfItemCommon,
  Cデータ納税予測,
  PlbscfデータYearValue,
  データApi,
  データRequest,
  勘定科目Api,
  部門Api,
} from '@/openapi/user';

import KeystrokeInputableText from '@/components/keystrokeInputableText/KeystrokeInputableText.vue';
import MoneyUnitSelect from '@/components/moneyUnitSelect/MoneyUnitSelect.vue';
import MoneyUnit決算期TableHeader from '@/components/moneyUnit決算期TableHeader/MoneyUnit決算期TableHeader.vue';
import C保存Button from '@/components/保存Button/保存Button.vue';

import { InputableTextMixin } from '@/components/inputableText/InputableText';
import { KeystrokeInputableTextId } from '@/components/keystrokeInputableText/KeystrokeInputableText';

import KeystrokeInputableTextMixin from '@/entries/user/mixins/KeystrokeInputableTextMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 納税予測Service, {
  Cell,
  CellTagType,
  ListItemConfig,
  納税予測,
  納税予測ListItem,
} from '@/classes/service/計画/納税予測Service';
import AbstractデータService from '@/classes/service/AbstractデータService';

export default mixins(InputableTextMixin, KeystrokeInputableTextMixin, 計画書Mixin, 事業所Mixin).extend({
  name: 'P納税予測',

  components: {
    KeystrokeInputableText,
    MoneyUnitSelect,
    MoneyUnit決算期TableHeader,
    C保存Button,
  },

  data(): {
    api: {
      d: データApi;
      k: 勘定科目Api;
      b: 部門Api;
    };
    readonly cellTagType: typeof CellTagType;
    service: 納税予測Service;
    v納税予測: 納税予測 | null;
    isEditing: boolean;
  } {
    return {
      api: {
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
        b: UserApiUtility.ogApi(部門Api),
      },
      cellTagType: CellTagType,
      service: new 納税予測Service(Eフェーズ種別.計画フェーズ),
      v納税予測: null,
      isEditing: false,
    };
  },

  filters: {
    rowCss(config: ListItemConfig): string {
      switch (config) {
        case ListItemConfig.subHeader:
          return 'specialHeaderRow';
        case ListItemConfig.subTotal:
        case ListItemConfig.input期首残高:
          return 'subHeaderRow';
        case ListItemConfig.total:
          return 'majorHeaderRow';
        default:
          return '';
      }
    },
    cellCss(cell: Cell): string {
      switch (cell.tagType) {
        case CellTagType.inputCurrency:
          return 'white text-right';
        case CellTagType.inputText:
          return 'white';
        case CellTagType.inputPercent:
          return 'white per text-right';
        case CellTagType.inputTotalCurrency:
        case CellTagType.currency:
          return 'text-right';
        case CellTagType.text:
          return cell.unit !== undefined ? 'text-right' : '';
        default:
          return '';
      }
    },
  },

  computed: {
    map(): (KeystrokeInputableTextId | null)[][] {
      if (this.v納税予測 === null) return [];

      const map: (KeystrokeInputableTextId | null)[][] = [];
      this.v納税予測.tables.forEach((table, i) => {
        table.items.forEach((item, j) => {
          // 入力行でない場合はmapに入れない
          if (
            item.config !== ListItemConfig.input &&
            item.config !== ListItemConfig.input期首残高 &&
            item.config !== ListItemConfig.inputTotal
          )
            return;

          const ids: (KeystrokeInputableTextId | null)[] = [];
          item.cells.forEach((cell, k) => {
            ids.push(cell.tagType === CellTagType.text || cell.tagType === CellTagType.currency ? null : { i, j, k });
          });
          map.push(ids);
        });
      });
      return map;
    },
  },

  watch: {
    'v納税予測.cデータ': {
      handler(_newVal: Cデータ納税予測, oldVal: Cデータ納税予測): void {
        if (this.v納税予測 === null) return;
        if (oldVal !== undefined) this.isEditing = true;
        this.v納税予測.tables = this.service.createTables(this.v納税予測.cデータ);
      },
      deep: true,
    },
  },

  async created(): Promise<void> {
    const 部門Request = this.api.b.list部門(this.事業所.pk);
    const 勘定科目Request = this.api.k.list勘定科目(this.事業所.pk);
    const データRequest = this.api.d.listデータ(this.計画書.pk, this.service.getRelationalE入力分類s(), undefined, {
      params: {
        sj: '納税予測',
      },
    });
    const responses = await Promise.all([部門Request, 勘定科目Request, データRequest]);
    this.service.set部門s = responses[0].data;
    this.service.set勘定科目s = responses[1].data;
    this.v納税予測 = this.service.parse(responses[2].data);

    this.setFirstKeystrokeId(this.map);
  },

  methods: {
    getColspan(config: ListItemConfig): number {
      if (this.v納税予測 === null) return 1;
      return config === ListItemConfig.subHeader ? this.v納税予測.header.length + 1 : 1;
    },

    change(cell: Cell, v: string | number): void {
      const ref = cell.value.reference;
      if (ref !== undefined) this.$set(ref.object, ref.key, v);
    },

    async save(): Promise<void> {
      if (this.v納税予測 === null) return;
      // リクエスト生成
      const データpk = this.v納税予測.データpk;
      const request: データRequest = {
        c入力分類: E入力分類.納税予測,
        c番号: 0,
        cフェーズ種別: Eフェーズ種別.計画フェーズ,
        cデータ: JSON.stringify(this.v納税予測.cデータ),
      };
      // API呼び出し
      データpk === undefined
        ? await this.api.d.createデータ(this.計画書.pk, request)
        : await this.api.d.updateデータ(データpk, request);

      this.isEditing = false;
    },

    async savePlbscf(): Promise<void> {
      if (this.v納税予測 === null) return;

      // yearValues準備
      const yearValues: PlbscfデータYearValue[] = [];

      // 指定の表とラベルから納税予測ListItemを取得する関数
      const getListItem = (tableIndex: number, e: E集計転記科目): 納税予測ListItem | undefined => {
        return this.v納税予測!.tables[tableIndex]?.items.find((item) => item.cells[0].value.fix === e);
      };
      // 一覧から該当の行を取得
      const 法人税等ListItem = getListItem(0, E集計転記科目.法人税等)!;
      const 未払法人税等ListItem = getListItem(0, E集計転記科目.未払法人税等)!;
      const 未払消費税等ListItem = getListItem(2, E集計転記科目.未払消費税等);

      // 勘定科目PKを取得する関数
      const get勘定科目Pk = (e: E集計転記科目): number => {
        return this.service.get勘定科目s.find((item) => item.c集計転記科目 === e)!.pk;
      };
      // 勘定科目PKを取得
      const v勘定科目PkOf法人税等 = get勘定科目Pk(E集計転記科目.法人税等);
      const v勘定科目PkOf未払法人税等 = get勘定科目Pk(E集計転記科目.未払法人税等);
      const v勘定科目PkOf未払消費税等 = get勘定科目Pk(E集計転記科目.未払消費税等);

      // リクエストのyearlyValuesのvaluesの要素を作成する関数
      const createValue = (
        listItem: 納税予測ListItem,
        勘定科目Pk: number,
        期数: number,
        year?: number // BS(貸借)の場合のみ設定
      ): CデータPlbscfItemCommon => {
        const yearlyValue = Number(this.service.getCellValue(listItem.cells[期数 + 1]));
        const value: CデータPlbscfItemCommon = {
          勘定科目Pk: 勘定科目Pk,
          value: yearlyValue,
        };
        // 計画0, 1期の時はmonthlyValuesを作成
        if (期数 < 2) {
          if (year === undefined) {
            AbstractデータService.divideYearlyValue(value, yearlyValue);
          } else {
            this.service.setMonthlyValuesFor貸借(value, 勘定科目Pk, yearlyValue, year);
          }
        }
        return value;
      };

      // 決算期数分のyearValuesを作成
      this.v納税予測.header.forEach((h, i) => {
        // PL計画に転記する項目をyearValuesに追加(法人税等)
        yearValues.push({
          year: h.year,
          c部門pK: this.service.get部門s[0].pk,
          values: [createValue(法人税等ListItem, v勘定科目PkOf法人税等, i)],
        });

        // BS計画に転記する項目をyearValuesに追加(未払法人税等 / 未払消費税等) ※未払消費税等はある時だけ
        const values = [createValue(未払法人税等ListItem, v勘定科目PkOf未払法人税等, i, h.year)];
        if (未払消費税等ListItem !== undefined) {
          // 未払消費税等がある場合は配列に追加
          values.push(createValue(未払消費税等ListItem, v勘定科目PkOf未払消費税等, i, h.year));
        }
        yearValues.push({
          year: h.year,
          values,
        });
      });

      // API呼び出し
      await this.api.d.savePlbscfデータ(this.計画書.pk, {
        cフェーズ種別: Eフェーズ種別.計画フェーズ,
        yearValues: yearValues,
      });
    },
  },
});
